import { Component, EventEmitter, forwardRef, Input, OnChanges, OnInit, Output, SimpleChanges } from '@angular/core';
import { ControlValueAccessor, NG_VALUE_ACCESSOR } from '@angular/forms';
import { cloneDeep, uniq } from 'lodash';
import { CrudFormConfig, CrudFormItem } from '../../../utils/edit-form';
import { FormLayout } from '../../../utils/edit-form/form-layout.enum';
import { TextColorType } from '../../../utils/edit-form/crud-view-text-color-type.enum';
import { TextStyleType } from '../../../utils/edit-form/crud-view-text-style-type.enum';
import { ColumnNumber } from '../../crud-view/model/enums';
import { IconType } from '../../../utils/icon';
import { ButtonType } from '../../../utils/button';
import { SelectableOptionWithTooltip } from '../../../utils/form-utils/selectable-option.type';
import { SelectMode, SelectSettings } from '../../../utils/select';
import { OptionMode } from '../../../utils/select/select-option-mode.enum';
import {
  FieldFormattingContext,
  createValueContext,
  extractFieldNameFromValueContext,
  isAllFieldsContext,
  isAllValuesContext,
  isValueContext
} from '../../../utils/field-formatting/field-formatting-context.constants';
import { FieldFormattingService } from '../../../utils/field-formatting/field-formatting.service';

/**
 * Settings component for data detail view builder
 * Handles display column selector settings (matches detailViewConfig structure)
 */
@Component({
  selector: 'rappider-data-detail-view-builder-settings',
  templateUrl: './data-detail-view-builder-settings.component.html',
  styleUrls: ['./data-detail-view-builder-settings.component.scss'],
  providers: [
    {
      provide: NG_VALUE_ACCESSOR,
      useExisting: forwardRef(() => DataDetailViewBuilderSettingsComponent),
      multi: true
    }
  ]
})
export class DataDetailViewBuilderSettingsComponent implements OnInit, OnChanges, ControlValueAccessor {
  @Output() editJsonData = new EventEmitter<void>();
  @Input() previewColumnNumber: ColumnNumber;
  @Output() previewColumnNumberChange = new EventEmitter<ColumnNumber>();
  @Output() ngModelChange = new EventEmitter<CrudFormConfig>();

  _value: CrudFormConfig = {
    items: [],
    sections: []
  };

  // Default column number for preview
  defaultColumnNumber = ColumnNumber.TwoColumn;

  // Select component settings
  SelectMode = SelectMode;
  OptionMode = OptionMode;
  selectSettings: SelectSettings = {
    allowClear: true,
    searchable: true
  };

  compactSelectSettings: SelectSettings = {
    allowClear: true,
    searchable: false
  };

  columnNumberOptions: SelectableOptionWithTooltip[] = [
    { key: '1 Column', value: ColumnNumber.OneColumn },
    { key: '2 Columns', value: ColumnNumber.TwoColumn },
    { key: '3 Columns', value: ColumnNumber.ThreeColumn }
  ];

  FormLayout = FormLayout;
  ColumnNumber = ColumnNumber;
  IconType = IconType;
  ButtonType = ButtonType;
  TextColorType = TextColorType;
  TextStyleType = TextStyleType;
  FieldFormattingContext = FieldFormattingContext;
  createValueContext = createValueContext;

  currentFieldContext: string | null = null;
  availableFields: CrudFormItem[] = [];

  textColorOptions: SelectableOptionWithTooltip[] = [
    { key: 'Default', value: TextColorType.Default },
    { key: 'Warning', value: TextColorType.Warning },
    { key: 'Error', value: TextColorType.Error },
    { key: 'Success', value: TextColorType.Success },
    { key: 'Info', value: TextColorType.Info }
  ];

  styleButtons = [
    { type: TextStyleType.Bold, icon: 'fa-bold', tooltip: 'Bold' },
    { type: TextStyleType.Italic, icon: 'fa-italic', tooltip: 'Italic' },
    { type: TextStyleType.Underline, icon: 'fa-underline', tooltip: 'Underline' }
  ];

  constructor(private fieldFormattingService: FieldFormattingService) {
    this.updateAvailableFields();
  }

  get value(): CrudFormConfig {
    return this._value;
  }

  set value(value: CrudFormConfig) {
    this._value = value;
    this.onChange(value);
    this.onTouched();
    this.ngModelChange.emit(value);
    setTimeout(() => {
      this.updateAvailableFields();
    }, 0);
  }

  /**
   * Get text color value (delegates to service)
   */
  getTextColorValue(colorType: TextColorType | string | undefined): string {
    return this.fieldFormattingService.getTextColorValue(colorType);
  }

  /**
   * Get color indicator for dropdown menu (delegates to service)
   */
  getColorIndicator(colorType: TextColorType): string {
    return this.fieldFormattingService.getTextColorIndicator(colorType);
  }

  /**
   * Set current field when color button is clicked
   */
  setCurrentFieldContext(context: string): void {
    this.currentFieldContext = context;
  }

  /**
   * Toggle field style (Bold, Italic, Underline)
   */
  toggleFieldStyle(fieldName: string, styleType: TextStyleType): void {
    const currentStyle = this.getFieldTextStyle(fieldName);
    const newStyle = this.fieldFormattingService.calculateNewStyle(currentStyle, styleType);
    this.onFieldTextStyleChange(fieldName, newStyle);
  }

  /**
   * Check if all fields have the same color
   */
  hasAllFieldsSameColor(): boolean {
    return this.fieldFormattingService.hasAllFieldsSameProperty(
      this._value as any,
      this.availableFields as any,
      (config, fieldName) => this.fieldFormattingService.getFieldTextColor(config, fieldName)
    );
  }

  /**
   * Get color that all fields share (if they all have the same)
   */
  getAllFieldsColor(): TextColorType | string | undefined {
    return this.fieldFormattingService.getAllFieldsProperty(
      this._value as any,
      this.availableFields as any,
      (config, fieldName) => this.fieldFormattingService.getFieldTextColor(config, fieldName),
      this.hasAllFieldsSameColor()
    ) as TextColorType | string | undefined;
  }

  /**
   * Check if all fields have the same background color
   */
  hasAllFieldsSameBackgroundColor(): boolean {
    return this.fieldFormattingService.hasAllFieldsSameProperty(
      this._value as any,
      this.availableFields as any,
      (config, fieldName) => this.fieldFormattingService.getFieldBackgroundColor(config, fieldName)
    );
  }

  /**
   * Get background color that all fields share (if they all have the same)
   */
  getAllFieldsBackgroundColor(): TextColorType | string | undefined {
    return this.fieldFormattingService.getAllFieldsProperty(
      this._value as any,
      this.availableFields as any,
      (config, fieldName) => this.fieldFormattingService.getFieldBackgroundColor(config, fieldName),
      this.hasAllFieldsSameBackgroundColor()
    ) as TextColorType | string | undefined;
  }

  /**
   * Check if all fields have the same value background color
   */
  hasAllFieldsSameValueBackgroundColor(): boolean {
    return this.fieldFormattingService.hasAllFieldsSameProperty(
      this._value as any,
      this.availableFields as any,
      (config, fieldName) => this.fieldFormattingService.getFieldValueBackgroundColor(config, fieldName)
    );
  }

  /**
   * Get value background color that all fields share (if they all have the same)
   */
  getAllFieldsValueBackgroundColor(): TextColorType | string | undefined {
    return this.fieldFormattingService.getAllFieldsProperty(
      this._value as any,
      this.availableFields as any,
      (config, fieldName) => this.fieldFormattingService.getFieldValueBackgroundColor(config, fieldName),
      this.hasAllFieldsSameValueBackgroundColor()
    ) as TextColorType | string | undefined;
  }

  /**
   * Check if all fields have the same style
   */
  hasAllFieldsSameStyle(): boolean {
    return this.fieldFormattingService.hasAllFieldsSameProperty(
      this._value as any,
      this.availableFields as any,
      (config, fieldName) => this.fieldFormattingService.getFieldTextStyle(config, fieldName)
    );
  }

  /**
   * Get style that all fields share (if they all have the same)
   */
  getAllFieldsStyle(): TextStyleType | undefined {
    return this.fieldFormattingService.getAllFieldsProperty(
      this._value as any,
      this.availableFields as any,
      (config, fieldName) => this.fieldFormattingService.getFieldTextStyle(config, fieldName),
      this.hasAllFieldsSameStyle()
    ) as TextStyleType | undefined;
  }

  /**
   * Apply color to all fields
   */
  applyColorToAllFields(color: TextColorType | string | undefined): void {
    this.value = this.fieldFormattingService.applyColorToAllFields(this._value as any, this.availableFields as any, color) as CrudFormConfig;
  }

  /**
   * Apply background color to all fields
   */
  applyBackgroundColorToAllFields(color: TextColorType | string | undefined): void {
    this.value = this.fieldFormattingService.applyBackgroundColorToAllFields(this._value as any, this.availableFields as any, color) as CrudFormConfig;
  }

  /**
   * Apply background color to all field values
   */
  applyBackgroundColorToAllFieldValues(color: TextColorType | string | undefined): void {
    this.value = this.fieldFormattingService.applyBackgroundColorToAllFieldValues(this._value as any, this.availableFields as any, color) as CrudFormConfig;
  }

  /**
   * Handle field value background color change
   */
  onFieldValueBackgroundColorChange(fieldName: string, color: TextColorType | string | undefined): void {
    this.value = this.fieldFormattingService.updateFieldValueBackgroundColor(this._value as any, fieldName, color) as CrudFormConfig;
  }

  /**
   * Toggle style for all fields
   */
  toggleAllFieldsStyle(styleType: TextStyleType): void {
    const currentStyle = this.hasAllFieldsSameStyle() ? this.getAllFieldsStyle() : undefined;
    this.value = this.fieldFormattingService.toggleAllFieldsStyle(this._value as any, this.availableFields as any, currentStyle, styleType) as CrudFormConfig;
  }

  ngOnInit(): void {
    if (!this._value) {
      this._value = {
        items: [],
        sections: []
      };
    }
    this.updateAvailableFields();
  }

  ngOnChanges(_changes: SimpleChanges): void {
    this.updateAvailableFields();
  }

  onChange: (value: CrudFormConfig) => void = () => { };
  onTouched: () => void = () => { };

  writeValue(value: CrudFormConfig): void {
    if (!value) {
      this._value = {
        items: [],
        sections: []
      };
    } else {
      this._value = cloneDeep(value);
    }
    setTimeout(() => {
      this.updateAvailableFields();
    }, 0);
  }

  registerOnChange(fn: (value: CrudFormConfig) => void): void {
    this.onChange = fn;
  }

  registerOnTouched(fn: () => void): void {
    this.onTouched = fn;
  }

  /**
   * Handle show in collapsed sections toggle
   */
  onShowInCollapsedSectionsChange(enabled: boolean): void {
    this.value = { ...this.value, showInCollapsedSections: enabled };
  }

  /**
   * Get default section title from config
   */
  getDefaultSectionTitle(): string {
    return this._value?.defaultSectionTitle || '';
  }

  /**
   * Handle default section title change
   * Updates defaultSectionTitle and also updates sections that don't have explicit titles
   */
  onDefaultSectionTitleChange(title: string): void {
    const trimmedTitle = title?.trim();
    const newDefaultTitle = trimmedTitle && trimmedTitle.length > 0 ? trimmedTitle : undefined;
    this.value = {
      ...this.value,
      defaultSectionTitle: newDefaultTitle
    };
  }

  /**
   * Get field text color from config
   */
  getFieldTextColor(fieldName: string): TextColorType | string | undefined {
    return this.fieldFormattingService.getFieldTextColor(this._value as any, fieldName);
  }

  /**
   * Get field background color from config (for field labels)
   */
  getFieldBackgroundColor(fieldName: string): TextColorType | string | undefined {
    return this.fieldFormattingService.getFieldBackgroundColor(this._value as any, fieldName);
  }

  /**
   * Get field value background color from config (for field values)
   */
  getFieldValueBackgroundColor(fieldName: string): TextColorType | string | undefined {
    return this.fieldFormattingService.getFieldValueBackgroundColor(this._value as any, fieldName);
  }

  /**
   * Handle field text color change
   */
  onFieldTextColorChange(fieldName: string, color: TextColorType | string | undefined): void {
    this.value = this.fieldFormattingService.updateFieldTextColor(this._value as any, fieldName, color) as CrudFormConfig;
  }

  /**
   * Handle field background color change
   */
  onFieldBackgroundColorChange(fieldName: string, color: TextColorType | string | undefined): void {
    this.value = this.fieldFormattingService.updateFieldBackgroundColor(this._value as any, fieldName, color) as CrudFormConfig;
  }

  /**
   * Get field text style from config
   */
  getFieldTextStyle(fieldName: string): TextStyleType | undefined {
    return this.fieldFormattingService.getFieldTextStyle(this._value as any, fieldName);
  }

  /**
   * Handle field text style change
   */
  onFieldTextStyleChange(fieldName: string, style: TextStyleType | undefined): void {
    this.value = this.fieldFormattingService.updateFieldTextStyle(this._value as any, fieldName, style) as CrudFormConfig;
  }

  /**
   * Get field value text color from config
   */
  getFieldValueTextColor(fieldName: string): TextColorType | string | undefined {
    return this.fieldFormattingService.getFieldValueTextColor(this._value as any, fieldName);
  }

  /**
   * Handle field value text color change
   */
  onFieldValueTextColorChange(fieldName: string, color: TextColorType | string | undefined): void {
    this.value = this.fieldFormattingService.updateFieldValueTextColor(this._value as any, fieldName, color) as CrudFormConfig;
  }

  /**
   * Get field value text style from config
   */
  getFieldValueTextStyle(fieldName: string): TextStyleType | undefined {
    return this.fieldFormattingService.getFieldValueTextStyle(this._value as any, fieldName);
  }

  /**
   * Handle field value text style change
   */
  onFieldValueTextStyleChange(fieldName: string, style: TextStyleType | undefined): void {
    this.value = this.fieldFormattingService.updateFieldValueTextStyle(this._value as any, fieldName, style) as CrudFormConfig;
  }

  /**
   * Toggle field value style (Bold, Italic, Underline)
   */
  toggleFieldValueStyle(fieldName: string, styleType: TextStyleType): void {
    const currentStyle = this.getFieldValueTextStyle(fieldName);
    const newStyle = this.fieldFormattingService.calculateNewStyle(currentStyle, styleType);
    this.onFieldValueTextStyleChange(fieldName, newStyle);
  }

  /**
   * Check if all fields have the same value color
   */
  hasAllFieldsSameValueColor(): boolean {
    return this.fieldFormattingService.hasAllFieldsSameProperty(
      this._value as any,
      this.availableFields as any,
      (config, fieldName) => this.fieldFormattingService.getFieldValueTextColor(config, fieldName)
    );
  }

  /**
   * Get value color that all fields share (if they all have the same)
   */
  getAllFieldsValueColor(): TextColorType | string | undefined {
    return this.fieldFormattingService.getAllFieldsProperty(
      this._value as any,
      this.availableFields as any,
      (config, fieldName) => this.fieldFormattingService.getFieldValueTextColor(config, fieldName),
      this.hasAllFieldsSameValueColor()
    ) as TextColorType | string | undefined;
  }

  /**
   * Check if all fields have the same value style
   */
  hasAllFieldsSameValueStyle(): boolean {
    return this.fieldFormattingService.hasAllFieldsSameProperty(
      this._value as any,
      this.availableFields as any,
      (config, fieldName) => this.fieldFormattingService.getFieldValueTextStyle(config, fieldName)
    );
  }

  /**
   * Get value style that all fields share (if they all have the same)
   */
  getAllFieldsValueStyle(): TextStyleType | undefined {
    return this.fieldFormattingService.getAllFieldsProperty(
      this._value as any,
      this.availableFields as any,
      (config, fieldName) => this.fieldFormattingService.getFieldValueTextStyle(config, fieldName),
      this.hasAllFieldsSameValueStyle()
    ) as TextStyleType | undefined;
  }

  /**
   * Apply color to all field values
   */
  applyColorToAllFieldValues(color: TextColorType | string | undefined): void {
    this.value = this.fieldFormattingService.applyColorToAllFieldValues(this._value as any, this.availableFields as any, color) as CrudFormConfig;
  }

  /**
   * Toggle style for all field values
   */
  toggleAllFieldValuesStyle(styleType: TextStyleType): void {
    const currentStyle = this.hasAllFieldsSameValueStyle() ? this.getAllFieldsValueStyle() : undefined;
    this.value = this.fieldFormattingService.toggleAllFieldValuesStyle(this._value as any, this.availableFields as any, currentStyle, styleType) as CrudFormConfig;
  }

  /**
   * Track by function for field list
   */
  trackByFieldName(index: number, field: CrudFormItem): string {
    return field.fieldName || `field-${index}`;
  }

  /**
   * Open JSON editor
   */
  onEditJsonData(): void {
    this.editJsonData.emit();
  }

  /**
   * Handle preview column number change
   * Updates previewColumnNumber and also updates sections columns property
   */
  onPreviewColumnNumberChange(columnNumber: ColumnNumber): void {
    this.previewColumnNumber = columnNumber;
    this.previewColumnNumberChange.emit(columnNumber);
    const columnCount = columnNumber as number;
    let sections = this._value.sections ? [...this._value.sections] : [];
    if (sections.length > 0) {
      sections = sections.map(section => ({
        ...section,
        columns: columnCount
      }));
    } else {
      if (this._value.items && this._value.items.length > 0) {
        const sectionNames = this._value.items
          .map(item => item.section)
          .filter(section => section && typeof section === 'string')
          .map(section => section.trim())
          .filter(section => section.length > 0);
        const uniqueSectionNames = uniq(sectionNames);

        if (uniqueSectionNames.length > 0) {
          sections = uniqueSectionNames.map(sectionName => ({
            key: sectionName,
            columns: columnCount
          }));
        }
      }
    }

    this.value = {
      ...this.value,
      sections: sections
    };
  }

  /**
   * Handle formatting color change - routes to appropriate handler based on context
   * Supports both TextColorType enum and hex color strings
   */
  onFormattingColorChange(color: TextColorType | string | undefined): void {
    if (!this.currentFieldContext) {
      return;
    }

    if (isAllFieldsContext(this.currentFieldContext)) {
      this.applyColorToAllFields(color as TextColorType);
    } else if (isAllValuesContext(this.currentFieldContext)) {
      this.applyColorToAllFieldValues(color as TextColorType);
    } else if (isValueContext(this.currentFieldContext)) {
      const fieldName = extractFieldNameFromValueContext(this.currentFieldContext);
      this.onFieldValueTextColorChange(fieldName, color as TextColorType | string);
    } else {
      this.onFieldTextColorChange(this.currentFieldContext, color as TextColorType | string);
    }
  }

  /**
   * Handle formatting background color change - routes to appropriate handler based on context
   * Supports both TextColorType enum and hex color strings
   */
  onFormattingBackgroundColorChange(color: TextColorType | string | undefined): void {
    if (!this.currentFieldContext) {
      return;
    }

    if (isAllFieldsContext(this.currentFieldContext)) {
      this.applyBackgroundColorToAllFields(color as TextColorType | string);
    } else if (isAllValuesContext(this.currentFieldContext)) {
      this.applyBackgroundColorToAllFieldValues(color as TextColorType | string);
    } else if (isValueContext(this.currentFieldContext)) {
      const fieldName = extractFieldNameFromValueContext(this.currentFieldContext);
      this.onFieldValueBackgroundColorChange(fieldName, color as TextColorType | string);
    } else {
      this.onFieldBackgroundColorChange(this.currentFieldContext, color as TextColorType | string);
    }
  }

  /**
   * Handle formatting style toggle - routes to appropriate handler based on context
   */
  onFormattingStyleToggle(styleType: TextStyleType): void {
    if (!this.currentFieldContext) {
      return;
    }

    if (isAllFieldsContext(this.currentFieldContext)) {
      this.toggleAllFieldsStyle(styleType);
    } else if (isAllValuesContext(this.currentFieldContext)) {
      this.toggleAllFieldValuesStyle(styleType);
    } else if (isValueContext(this.currentFieldContext)) {
      const fieldName = extractFieldNameFromValueContext(this.currentFieldContext);
      this.toggleFieldValueStyle(fieldName, styleType);
    } else {
      this.toggleFieldStyle(this.currentFieldContext, styleType);
    }
  }

  /**
   * Check if a color option is selected based on current context
   */
  isColorOptionSelected(colorValue: TextColorType): boolean {
    if (!this.currentFieldContext) {
      return false;
    }

    if (isAllFieldsContext(this.currentFieldContext)) {
      return this.hasAllFieldsSameColor() && this.getAllFieldsColor() === colorValue;
    } else if (isAllValuesContext(this.currentFieldContext)) {
      return this.hasAllFieldsSameValueColor() && this.getAllFieldsValueColor() === colorValue;
    } else if (isValueContext(this.currentFieldContext)) {
      const fieldName = extractFieldNameFromValueContext(this.currentFieldContext);
      return this.getFieldValueTextColor(fieldName) === colorValue;
    } else {
      return this.getFieldTextColor(this.currentFieldContext) === colorValue;
    }
  }


  /**
   * Update available fields from items
   */
  private updateAvailableFields(): void {
    this.availableFields = this._value?.items?.filter(item => item.visible !== false) || [];
  }
}

